import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone,
  Heart,
  Brain,
  Users,
  Shield,
  ChevronRight,
  Plus,
  Edit,
  Trash2,
  Mail,
  MapPin,
  Star,
  Search,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { EmergencyService, EmergencyContact, EmergencyProfile } from '../../lib/emergencyService';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { SearchInput } from '../common/SearchInput';

const roleLabels: Record<EmergencyContact['role'], string> = {
  primary_contact: 'Primary Contact',
  backup: 'Backup Contact',
  neighbour: 'Neighbour',
  gp: 'General Practitioner',
  school: 'School',
  insurer: 'Insurer',
  employer: 'Employer',
  pet_carer: 'Pet Carer'
};

export function EmergencyContactsList() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [profile, setProfile] = useState<EmergencyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState<EmergencyContact | null>(null);

  useEffect(() => {
    loadProfileAndContacts();
  }, [user]);

  const loadProfileAndContacts = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get or create active profile
      let activeProfile = await EmergencyService.getActiveProfile(user.id);
      
      if (!activeProfile) {
        activeProfile = await EmergencyService.createProfile({
          user_id: user.id,
          type: 'personal',
          status: 'active',
          locale: 'en-AU',
          name: 'My Emergency Plan',
          description: 'Personal emergency preparedness plan'
        });
        await EmergencyService.activateProfile(activeProfile.id);
      }
      
      setProfile(activeProfile);
      
      // Load contacts
      const contactsData = await EmergencyService.getContacts(activeProfile.id);
      setContacts(contactsData);
    } catch (err) {
      console.error('Error loading contacts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load emergency contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContact = async (contact: Omit<EmergencyContact, 'id' | 'created_at' | 'updated_at'>) => {
    if (!profile) return;
    
    try {
      const newContact = await EmergencyService.createContact({
        ...contact,
        profile_id: profile.id
      });
      setContacts(prev => [...prev, newContact]);
      setShowAddModal(false);
    } catch (err) {
      console.error('Error creating contact:', err);
      setError(err instanceof Error ? err.message : 'Failed to create contact');
    }
  };

  const handleUpdateContact = async (contact: EmergencyContact) => {
    try {
      const updatedContact = await EmergencyService.updateContact(contact.id, contact);
      setContacts(prev => prev.map(c => c.id === updatedContact.id ? updatedContact : c));
      setEditingContact(null);
    } catch (err) {
      console.error('Error updating contact:', err);
      setError(err instanceof Error ? err.message : 'Failed to update contact');
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!confirm('Are you sure you want to delete this emergency contact?')) {
      return;
    }

    try {
      await EmergencyService.deleteContact(contactId);
      setContacts(prev => prev.filter(c => c.id !== contactId));
    } catch (err) {
      console.error('Error deleting contact:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete contact');
    }
  };

  const handleCall = (number: string) => {
    window.location.href = `tel:${number}`;
  };

  const filteredContacts = contacts.filter(contact => {
    return contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           (contact.phone && contact.phone.includes(searchQuery)) ||
           (contact.email && contact.email.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-64 bg-gray-200 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Emergency Contacts</h1>
            <p className="text-xl text-gray-600">Manage your emergency contact network</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            variant="primary"
            className="flex items-center space-x-2 px-6 py-3 text-lg"
          >
            <Plus size={20} />
            <span>Add Contact</span>
          </Button>
        </div>

        {/* Search */}
        <div className="mb-8">
          <SearchInput
            placeholder="Search contacts by name, phone, or email..."
            value={searchQuery}
            onChange={setSearchQuery}
            icon={<Search size={18} />}
            className="text-lg"
          />
        </div>

        {/* Contacts Grid */}
        <AnimatePresence>
          {filteredContacts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-white border-0 shadow-xl">
                <div className="text-center py-16">
                  <div className="mx-auto w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mb-6">
                    <Users className="h-12 w-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {searchQuery ? 'No contacts found' : 'No emergency contacts yet'}
                  </h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto">
                    {searchQuery 
                      ? 'Try adjusting your search criteria'
                      : 'Start building your emergency contact network by adding your first contact'
                    }
                  </p>
                  <Button
                    onClick={() => setShowAddModal(true)}
                    variant="primary"
                    className="flex items-center space-x-2 px-8 py-3 text-lg mx-auto"
                  >
                    <Plus size={20} />
                    <span>Add Your First Contact</span>
                  </Button>
                </div>
              </Card>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredContacts.map((contact, index) => (
                <motion.div
                  key={contact.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card className="h-full p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                          {contact.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{contact.name}</h3>
                          <p className="text-sm text-gray-500">{roleLabels[contact.role]}</p>
                        </div>
                      </div>
                      {(contact.is_primary || contact.is_backup) && (
                        <Star className="text-yellow-500 fill-yellow-500" size={20} />
                      )}
                    </div>

                    <div className="space-y-3 mb-4">
                      {contact.phone && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Phone size={16} className="text-gray-500" />
                          <span className="font-medium">{contact.phone}</span>
                        </div>
                      )}
                      {contact.email && (
                        <div className="flex items-center gap-2 text-gray-700">
                          <Mail size={16} className="text-gray-500" />
                          <span className="text-sm truncate">{contact.email}</span>
                        </div>
                      )}
                      {contact.address && (
                        <div className="flex items-start gap-2 text-gray-700">
                          <MapPin size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm line-clamp-2">{contact.address}</span>
                        </div>
                      )}
                      {contact.relationship && (
                        <div className="text-sm text-gray-500">
                          Relationship: {contact.relationship}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pt-4 border-t border-gray-100">
                      {contact.phone && (
                        <Button
                          variant="primary"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleCall(contact.phone!)}
                        >
                          <Phone size={16} className="mr-2" />
                          Call
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingContact(contact)}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleDeleteContact(contact.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2 mt-6">
            <AlertCircle size={20} />
            <p>{error}</p>
          </div>
        )}

        {/* Add/Edit Contact Modal - Simplified for now */}
        {(showAddModal || editingContact) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowAddModal(false); setEditingContact(null); }}>
            <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingContact ? 'Edit Emergency Contact' : 'Add Emergency Contact'}
              </h2>
              <p className="text-gray-600 mb-4">
                Contact form will be implemented here. For now, you can manage contacts through the database.
              </p>
              <div className="flex justify-end gap-4">
                <Button variant="outline" onClick={() => { setShowAddModal(false); setEditingContact(null); }}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

